/** Camera/QR helpers for {@link CameraScanner}. Kept in a separate module (not the component
 * file) so the component file only exports a component — otherwise React Fast Refresh can't
 * preserve component state across edits (react-doctor: only-export-components). */
import { t } from '../i18n'

/** Minimal shape of the (optional) native BarcodeDetector we rely on. */
export interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>
}
interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike
}

/** Returns a QR-only BarcodeDetector if the host supports it, else null. */
export function createBarcodeDetector(): BarcodeDetectorLike | null {
  const Ctor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector
  if (!Ctor) return null
  try {
    return new Ctor({ formats: ['qr_code'] })
  } catch {
    return null
  }
}

/** Human-readable message for a getUserMedia / camera failure. */
export function cameraErrorMessage(error: unknown): string {
  const name = (error as { name?: string } | null)?.name
  if (name === 'NotAllowedError' || name === 'SecurityError') return t('camPermissionDenied')
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return t('camNotFound')
  if (name === 'NotReadableError') return t('camInUse')
  return error instanceof Error ? error.message : t('camGeneric')
}
